import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useExpanded,
  usePagination,
} from "react-table";
import { Table, Row, Col, Button, Spinner } from "reactstrap";
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter";
import { Link, useLocation } from "react-router-dom";

function GlobalFilter({
  globalFilter,
  setGlobalFilter,
  isJobListGlobalFilter,
  dataList,
  onSearch,
  onFilterChange,
  filterValue,
  onDateRangeChange,
  dateRangeValue,
  selectTitle,
}) {
  const [value, setValue] = useState(globalFilter);

  useEffect(() => {
    setValue(globalFilter || "")
  }, [globalFilter])

  const emit = useAsyncDebounce((val) => {
    setGlobalFilter(val || undefined);
    onSearch?.(val || "");
  }, 500);

  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <React.Fragment>
      <Col className="col-12 d-flex flex-column flex-lg-row align-items-center justify-content-between">
        <Col
          xxl={3}
          lg={6}
          className="mb-0 d-flex justify-content-between align-items-center me-lg-2 me-0 px-lg-0 px-1 col-12 col-lg-auto"
        >
          <div className="col-12 col-lg-auto flex-grow-1">
            <div className="position-relative">
              <input
                className="form-control"
                id="search-bar-0"
                value={value || ""}
                placeholder="Search..."
                autoFocus={false}
                onChange={(e) => {
                  const v = e.target.value;
                  setValue(v);
                  emit(v);
                }}
                style={{ paddingRight: "2.5rem", borderRadius: "10px" }}
              />
              {value ? (
                <i
                  className="mdi mdi-close icon"
                  style={{ color: "#06427a" }}
                  onClick={() => {
                    setValue("");
                    setGlobalFilter(undefined);
                    emit("");
                  }}
                />
              ) : (
                <i className="mdi mdi-magnify icon" style={{ color: "#06427a" }} />
              )}
            </div>
          </div>
        </Col>

        {isJobListGlobalFilter && (
          <JobListGlobalFilter
            setGlobalFilter={setGlobalFilter}
            dataList={dataList}
            onFilterChange={onFilterChange}
            filterValue={filterValue}
            onDateRangeChange={onDateRangeChange}
            dateRangeValue={dateRangeValue}
            selectTitle={
              selectTitle ??
              (location.pathname === "/resources"
                ? "Select Company"
                : location.pathname === "/companies"
                  ? "Select Resource"
                  : "Select Domain")
            }
          />
        )}
      </Col>
    </React.Fragment>
  );
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isJobListGlobalFilter,
  isPagination,
  iscustomPageSizeOptions,
  customPageSize,
  totalPages,
  manual,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSearch,
  searchValue,
  onFilterChange,
  filterValue,
  filterData,
  loading,
  onDateRangeChange,
  dateRangeValue,
  clearFilters,
  selectTitle,
}) => {
  const table = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: pageIndex || 0,
        pageSize: pageSize || customPageSize || 10,
        globalFilter: searchValue || "",
      },
      manualPagination: !!manual,
      manualGlobalFilter: !!manual,
      pageCount: manual ? totalPages : undefined,
      autoResetPage: false,
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    gotoPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = table

  useEffect(() => {
    if (manual) {
      table.setPageSize(pageSize)
      gotoPage(pageIndex)
    }
  }, [manual, pageIndex, pageSize, gotoPage, table])

  useEffect(() => {
    setGlobalFilter(searchValue || "");
  }, [searchValue, setGlobalFilter]);

  const handleGoto = (i) => {
    gotoPage(i)
    onPageChange?.(i)
  }

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };

  return (
    <Fragment>
      <Row className="mb-2">
        {iscustomPageSizeOptions && (
          <Col md={2}>
            <select
              className="form-select"
              value={pageSize}
              onChange={onChangeInSelect}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
            dataList={filterData}
            onSearch={onSearch}
            onFilterChange={onFilterChange}
            filterValue={filterValue}
            onDateRangeChange={onDateRangeChange}
            dateRangeValue={dateRangeValue}
            selectTitle={selectTitle}
          />
        )}
      </Row>

      {loading ?
        <div
          className="loading-container"
          style={{
            width: "100%",
            height: "60dvh",
            display: "grid",
            placeItems: "center",
          }}
        >
          <Spinner />
        </div> :

        <>
          {page?.length > 0 ? (
            <div className="table-responsive mt-4 pt-2">
              <Table {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th key={column.id} className={column.isSort ? "sorting" : ""}>
                          <div className="m-0" {...column.getSortByToggleProps()}>
                            {column.render("Header")}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <Fragment key={row.getRowProps().key}>
                        <tr>
                          {row.cells.map((cell) => (
                            <td className="align-middle" key={cell.id} {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div
              className="h2 mb-0 heading"
              style={{
                width: "100%",
                height: "58.6vh",
                display: "grid",
                placeItems: "center",
              }}
            >No Data Found</div>
          )}
        </>
      }
      {isPagination && totalPages > 1 && (
        <Row className="justify-content-between align-items-center">
          <div className="pagination pagination-rounded justify-content-end mb-2">
            <ul className="pagination">
              <li className={`page-item ${pageIndex <= 0 ? "disabled" : ""}`}>
                <Link to="#" className="page-link" onClick={() => handleGoto(pageIndex - 1)}>
                  <i className="mdi mdi-chevron-left"></i>
                </Link>
              </li>

              {Array.from({ length: totalPages }).map((_, i) => (
                <li key={i} className={pageIndex === i ? "page-item active" : "page-item"}>
                  <Link to="#" className="page-link" onClick={() => handleGoto(i)}>
                    {i + 1}
                  </Link>
                </li>
              ))}

              <li className={`page-item ${pageIndex >= totalPages - 1 ? "disabled" : ""}`}>
                <Link to="#" className="page-link" onClick={() => handleGoto(pageIndex + 1)}>
                  <i className="mdi mdi-chevron-right"></i>
                </Link>
              </li>
            </ul>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
